import React, { useEffect, useState } from "react";
import { Trash2, Settings2, Wrench, Clock, GitBranch, Plug, AlertTriangle } from "lucide-react";
import { TYPE_VISUAL } from "./FlowNodeCard";
import ConditionBuilder from "./ConditionBuilder";
import { api } from "@/lib/api";

/**
 * Right-side config panel for the selected flow node.
 *
 * The agent-node selector pulls from `agents` (passed from parent).
 * Validation is permissive: we only block save in the parent.
 */
export default function NodeConfigPanel({ node, onChange, onDelete, agents = [], upstreamNodes = [] }) {
    const [text, setText] = useState({
        input_mapping: JSON.stringify(node.input_mapping || {}, null, 2),
        config: JSON.stringify(node.config || {}, null, 2),
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        setText({
            input_mapping: JSON.stringify(node.input_mapping || {}, null, 2),
            config: JSON.stringify(node.config || {}, null, 2),
        });
        setErrors({});
    }, [node.id]); // eslint-disable-line

    const setJson = (field, raw) => {
        setText((t) => ({ ...t, [field]: raw }));
        try {
            const parsed = JSON.parse(raw || "null");
            setErrors((e) => ({ ...e, [field]: null }));
            onChange({ ...node, [field]: parsed });
        } catch (exc) {
            setErrors((e) => ({ ...e, [field]: exc.message }));
        }
    };

    const set = (patch) => onChange({ ...node, ...patch });
    const setConfig = (patch) => {
        const next = { ...(node.config || {}), ...patch };
        setText((t) => ({ ...t, config: JSON.stringify(next, null, 2) }));
        onChange({ ...node, config: next });
    };

    const visual = TYPE_VISUAL[node.type] || TYPE_VISUAL.transform;
    const Icon = visual.icon;

    return (
        <div className="border border-white/10 bg-[#0a0a0a] rounded-md p-4 w-80 max-h-full overflow-y-auto space-y-4" data-testid="node-config-panel">
            <div className="flex items-center justify-between pb-3 border-b border-white/10">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded flex items-center justify-center"
                        style={{ backgroundColor: `${visual.color}20`, color: visual.color }}>
                        <Icon className="w-3.5 h-3.5" strokeWidth={2} />
                    </div>
                    <div>
                        <div className="font-display text-sm tracking-tight">{visual.label}</div>
                        <div className="font-mono text-[10px] text-white/40">{node.id.slice(0, 8)}</div>
                    </div>
                </div>
                <button onClick={onDelete} data-testid="node-config-delete"
                    className="p-1.5 rounded hover:bg-red-500/20 text-white/60 hover:text-red-400">
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
            </div>

            {/* Identity */}
            <Section title="Identity" icon={<Settings2 className="w-3 h-3" />}>
                <Field label="Name">
                    <input value={node.name || ""} onChange={(e) => set({ name: e.target.value })}
                        data-testid="node-cfg-name" className="input text-xs" />
                </Field>
                <Field label="Description">
                    <textarea value={node.description || ""} onChange={(e) => set({ description: e.target.value })}
                        rows={2} className="input text-xs" />
                </Field>
            </Section>

            {/* Type-specific config */}
            {node.type === "agent" && (
                <Section title="Agent">
                    <Field label="Agent">
                        <select
                            value={(node.config || {}).agent_id || ""}
                            onChange={(e) => setConfig({ agent_id: e.target.value })}
                            data-testid="node-cfg-agent-id"
                            className="input text-xs"
                        >
                            <option value="">— choose an agent —</option>
                            {agents.map((a) => (
                                <option key={a.id} value={a.id}>{a.name} (v{a.active_version_no})</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Version (optional, defaults to active)">
                        <input
                            type="number" min="1"
                            value={(node.config || {}).version_no ?? ""}
                            onChange={(e) => setConfig({ version_no: e.target.value === "" ? null : Number(e.target.value) })}
                            className="input text-xs font-mono"
                        />
                    </Field>
                </Section>
            )}

            {(node.type === "webhook" || node.type === "api") && (
                <Section title={node.type === "webhook" ? "Webhook" : "HTTP API"}>
                    <Field label="URL">
                        <input
                            value={(node.config || {}).url || ""}
                            onChange={(e) => setConfig({ url: e.target.value })}
                            placeholder="https://…"
                            data-testid="node-cfg-url"
                            className="input text-xs font-mono"
                        />
                    </Field>
                    {node.type === "api" && (
                        <Field label="Method">
                            <select
                                value={(node.config || {}).method || "GET"}
                                onChange={(e) => setConfig({ method: e.target.value })}
                                data-testid="node-cfg-method"
                                className="input text-xs"
                            >
                                {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </Field>
                    )}
                </Section>
            )}

            {node.type === "condition" && (
                <Section title="If / Then" icon={<GitBranch className="w-3 h-3" />}>
                    <div className="text-[10px] text-white/50 mb-1">
                        When this expression is <strong>true</strong>, downstream nodes can read{" "}
                        <code className="text-[#7C3AED]">{`{{steps.${(node.id || "").slice(0, 8)}.output.branch}} == 'then'`}</code>.
                        On false, <code className="text-[#7C3AED]">'else'</code>.
                    </div>
                    <ConditionBuilder
                        value={(node.config || {}).expression || ""}
                        onChange={(expr) => setConfig({ expression: expr })}
                        upstreamNodes={upstreamNodes}
                        testIdPrefix="node-cfg-expression"
                    />
                </Section>
            )}

            {node.type === "protocol_intel" && (
                <Section title="Protocol Intelligence">
                    <Field label="Mode (which standardized JSON to return)">
                        <select
                            value={(node.config || {}).mode || "full"}
                            onChange={(e) => setConfig({ mode: e.target.value })}
                            data-testid="node-cfg-protocol-mode"
                            className="input text-xs"
                        >
                            <option value="full">full — everything</option>
                            <option value="eligibility">eligibility — inclusion + exclusion criteria</option>
                            <option value="cohorts">cohorts — study arms</option>
                            <option value="conmeds">conmeds — concomitant medications</option>
                            <option value="objectives_endpoints">objectives & endpoints</option>
                            <option value="schedule_of_events">schedule of events (matrix)</option>
                            <option value="visit_schedule">visit schedule</option>
                        </select>
                    </Field>
                    <Field label="Anchor visit override (optional — defaults to day_offset_from_baseline=0)">
                        <input
                            value={(node.config || {}).anchor_visit || ""}
                            onChange={(e) => setConfig({ anchor_visit: e.target.value || null })}
                            placeholder="Baseline"
                            data-testid="node-cfg-anchor-visit"
                            className="input text-xs font-mono"
                        />
                    </Field>
                    <div className="text-[10px] text-white/45">
                        Input must include either <code className="text-[#7C3AED]">protocol_id</code>{" "}
                        OR <code className="text-[#7C3AED]">client_id + protocol_number</code>{" "}
                        (with optional <code className="text-[#7C3AED]">version</code>).
                    </div>
                </Section>
            )}

            {node.type === "manual_approval" && (
                <Section title="Approval">
                    <Field label="Message">
                        <textarea
                            value={(node.config || {}).message || ""}
                            onChange={(e) => setConfig({ message: e.target.value })}
                            placeholder="Please approve before publishing"
                            rows={2}
                            className="input text-xs"
                        />
                    </Field>
                </Section>
            )}

            {node.type === "api_call" && (
                <ApiCallConfig
                    cfg={node.config || {}}
                    setConfig={setConfig}
                />
            )}

            {/* Mapping & raw config */}
            <Section title="Input mapping" icon={<Wrench className="w-3 h-3" />}>
                <textarea
                    value={text.input_mapping}
                    onChange={(e) => setJson("input_mapping", e.target.value)}
                    rows={5}
                    placeholder='{ "phrase": "{{input.text}}" }'
                    data-testid="node-cfg-input-mapping"
                    className="input text-[10px] font-mono"
                />
                {errors.input_mapping && <div className="text-[10px] text-red-400 mt-1">{errors.input_mapping}</div>}
                <div className="text-[10px] text-white/40 mt-1">
                    Use <code className="text-[#7C3AED]">{"{{input.x}}"}</code>, <code className="text-[#7C3AED]">{"{{steps.<id>.output.y}}"}</code>, <code className="text-[#7C3AED]">{"{{meta.client_id}}"}</code>.
                </div>
            </Section>

            <Section title="Raw config (advanced)">
                <textarea
                    value={text.config}
                    onChange={(e) => setJson("config", e.target.value)}
                    rows={5}
                    data-testid="node-cfg-raw"
                    className="input text-[10px] font-mono"
                />
                {errors.config && <div className="text-[10px] text-red-400 mt-1">{errors.config}</div>}
            </Section>

            {/* Skip condition / parallel group / retry / timeout */}
            <Section title="Execution control" icon={<Clock className="w-3 h-3" />}>
                <Field label="Skip condition (node skipped if expression is false; leave blank to always run)">
                    <ConditionBuilder
                        value={node.condition || ""}
                        onChange={(expr) => set({ condition: expr || null })}
                        upstreamNodes={upstreamNodes}
                        placeholder="{{input.shouldSkip}} != 'yes'"
                        testIdPrefix="node-cfg-condition"
                    />
                </Field>
                <Field label="Parallel group (nodes sharing this run via asyncio.gather)">
                    <input
                        value={node.parallel_group || ""}
                        onChange={(e) => set({ parallel_group: e.target.value || null })}
                        placeholder="e.g. lookup"
                        data-testid="node-cfg-parallel-group"
                        className="input text-[10px] font-mono"
                    />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                    <Field label="Retry max">
                        <input
                            type="number" min="1" max="10"
                            value={(node.retry || {}).max ?? 1}
                            onChange={(e) => set({ retry: { ...(node.retry || {}), max: Number(e.target.value) || 1 } })}
                            data-testid="node-cfg-retry-max"
                            className="input text-xs font-mono"
                        />
                    </Field>
                    <Field label="Backoff sec">
                        <input
                            type="number" min="0" step="0.5"
                            value={(node.retry || {}).backoff_seconds ?? 2}
                            onChange={(e) => set({ retry: { ...(node.retry || {}), backoff_seconds: Number(e.target.value) || 0 } })}
                            className="input text-xs font-mono"
                        />
                    </Field>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Field label="Timeout (s)">
                        <input
                            type="number" min="1"
                            value={node.timeout_seconds ?? 120}
                            onChange={(e) => set({ timeout_seconds: Number(e.target.value) || 120 })}
                            data-testid="node-cfg-timeout"
                            className="input text-xs font-mono"
                        />
                    </Field>
                    <Field label="Continue on failure">
                        <select
                            value={node.continue_on_failure ? "yes" : "no"}
                            onChange={(e) => set({ continue_on_failure: e.target.value === "yes" })}
                            className="input text-xs"
                        >
                            <option value="no">No (halt)</option>
                            <option value="yes">Yes</option>
                        </select>
                    </Field>
                </div>
            </Section>
        </div>
    );
}

function Section({ title, icon, children }) {
    return (
        <div className="space-y-2">
            <div className="label-xs text-white/40 flex items-center gap-1.5">{icon}{title}</div>
            {children}
        </div>
    );
}
function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-[10px] text-white/50 mb-1">{label}</div>
            {children}
        </label>
    );
}

function ApiCallConfig({ cfg, setConfig }) {
    const [conns, setConns] = useState([]);
    const [endpoints, setEndpoints] = useState([]);
    const [loadingConns, setLoadingConns] = useState(false);
    const [loadingEps, setLoadingEps] = useState(false);
    const connectionId = cfg.connection_id || "";
    const endpointId = cfg.endpoint_id || "";

    useEffect(() => {
        let cancel = false;
        (async () => {
            setLoadingConns(true);
            try {
                const { data } = await api.get("/admin/connections?status=published&limit=200");
                if (!cancel) setConns(data.items || []);
            } catch (_e) {
                if (!cancel) setConns([]);
            } finally {
                if (!cancel) setLoadingConns(false);
            }
        })();
        return () => { cancel = true; };
    }, []);

    useEffect(() => {
        let cancel = false;
        if (!connectionId) {
            setEndpoints([]);
            return undefined;
        }
        (async () => {
            setLoadingEps(true);
            try {
                const { data } = await api.get(`/admin/connections/${connectionId}/endpoints`);
                if (!cancel) setEndpoints(data.items || []);
            } catch (_e) {
                if (!cancel) setEndpoints([]);
            } finally {
                if (!cancel) setLoadingEps(false);
            }
        })();
        return () => { cancel = true; };
    }, [connectionId]);

    // Auto-clear a stale endpoint_id once the new connection's endpoint
    // list has loaded and confirms the previous selection is gone. This
    // is wrapped in useEffect (not done synchronously inside onConn) so
    // the parent's setConfig→setState chain runs *after* render — fixes
    // the "setState during render" console warning observed in iter32.
    useEffect(() => {
        if (!endpointId) return;
        if (loadingEps) return;
        if (!endpoints.find((e) => e.id === endpointId)) {
            setConfig({ endpoint_id: null });
        }
        // setConfig intentionally omitted from deps — it changes on every
        // parent render and would cause an infinite loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoints, loadingEps, endpointId]);

    const onConn = (id) => {
        // Only update connection_id here; the useEffect above handles the
        // endpoint_id reset asynchronously after the new endpoint list
        // resolves. This separation eliminates the React "setState during
        // render" warning.
        if (id !== connectionId) setConfig({ connection_id: id });
    };
    const selectedEp = endpoints.find((e) => e.id === endpointId);
    const selectedConn = conns.find((c) => c.id === connectionId);

    return (
        <Section title="API Call (Connection)" icon={<Plug className="w-3 h-3" />}>
            {selectedConn?.auth_type === "sitecentric_client_key" && (
                <div className="text-[10px] text-sky-200/90 border border-sky-300/20 bg-sky-300/[0.05] rounded px-2 py-1.5" data-testid="node-cfg-sitecentric-hint">
                    SiteCentric connection: the run's client key is exchanged for a session token once per flow run, reused by every tile and logged out when the run ends — start runs with a client selected.
                </div>
            )}
            <Field label="Connection (published only)">
                <select
                    value={connectionId}
                    onChange={(e) => onConn(e.target.value)}
                    data-testid="node-cfg-connection-id"
                    className="input text-xs"
                    disabled={loadingConns}
                >
                    <option value="">{loadingConns ? "Loading…" : "— choose a published connection —"}</option>
                    {conns.map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.name} {c.is_common ? "(common) " : ""}· v{c.version} · {c.environment}
                        </option>
                    ))}
                </select>
            </Field>
            <Field label="Endpoint">
                <select
                    value={endpointId}
                    onChange={(e) => setConfig({ endpoint_id: e.target.value || null })}
                    data-testid="node-cfg-endpoint-id"
                    className="input text-xs"
                    disabled={!connectionId || loadingEps}
                >
                    <option value="">
                        {!connectionId
                            ? "(select a connection first)"
                            : loadingEps ? "Loading…" : "— choose an endpoint —"}
                    </option>
                    {endpoints.map((e) => (
                        <option key={e.id} value={e.id}>
                            {e.method} {e.relative_path} · {e.timeout_s}s
                        </option>
                    ))}
                </select>
            </Field>
            <Field label="Output exposure">
                <select
                    value={cfg.expose || "body"}
                    onChange={(e) => setConfig({ expose: e.target.value })}
                    data-testid="node-cfg-expose"
                    className="input text-xs"
                >
                    <option value="body">body — parsed JSON only (recommended)</option>
                    <option value="envelope">envelope — body + statusCode + durationMs</option>
                    <option value="full">full — sanitised request + response (debug)</option>
                </select>
            </Field>
            {selectedConn && (
                <div className="text-[10px] text-white/45 leading-relaxed font-mono break-all">
                    {selectedConn.base_url}
                    {selectedEp ? selectedEp.relative_path : ""}
                </div>
            )}
            {selectedEp?.retry_policy?.enabled && (
                <div className="text-[10px] text-emerald-300/80 inline-flex items-center gap-1">
                    Retry on — {selectedEp.retry_policy.max_attempts}× backoff (breaker after {selectedEp.retry_policy.circuit_breaker_threshold} fails)
                </div>
            )}
            {connectionId && !loadingEps && endpoints.length === 0 && (
                <div className="text-[10px] text-amber-300 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> No endpoints published on this connection.
                </div>
            )}
            <div className="text-[10px] text-white/45">
                Body comes from the input mapping below (POST/PUT/PATCH).
                Path tokens like <code className="text-[#7C3AED]">{"{id}"}</code> are interpolated from the same mapping.
            </div>
        </Section>
    );
}
